import { RenderSkedCaption } from '@dialogueconsulting/sked-preview'
import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import startCase from 'lodash.startcase'

import {
  CarouselIcon,
  ReelIcon,
  VideoIcon,
  StoryIcon,
  TextIcon,
  ClockIcon,
} from 'Icons'
import { datePlaceholderForDraftPost, deMapPlatform } from 'normalizer'
import type { PostType } from 'Types'

import AvatarComponent from './Common/AvatarComponent'
import PlatformIcon from './Common/PlatformIcon'
import type { PlatformIconProps } from './Common/PlatformIcon'

export interface UpcomingPostCardProps {
  id: string
  status: string
  statusLabel?: string
  time: string
  accounts: {
    avatar: string
    name: string
    avatarBorderColor: string
  }[]
  caption: string
  platform?: PlatformIconProps['platform']
  media: {
    src?: string
    type: PostType
  }
  isUndo?: boolean
  undoClick: (postId: string, status: 'pending') => void
  isSelected?: boolean
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
  when?: string
}

// Status-colour helpers — exported and consumed by StatusLabel (mobile) and
// CalendarModal (kept here so their import paths are unchanged).
export const handleStatusStateBg = (status: string, isReadOnly: boolean) => {
  if (isReadOnly) {
    return '#4F31FF'
  }

  // SKE-3808: style-guide Badges/Chips — success / warning / error tints.
  switch (status) {
    case 'pending':
      return '#FEF3C7' // yellow-100 (warning)
    case 'approved':
      return '#D1FAE5' // green-100 (success)
    case 'requestChange':
      return '#FEDCD7' // red-100 (error)
    default:
      return '#E6E6E6' // neutral-100
  }
}

export const handleStatusStateColor = (status: string) => {
  // SKE-3808: dark on-tint text matching the style-guide chip variants.
  switch (status) {
    case 'pending':
      return '#B45309' // yellow-700
    case 'approved':
      return '#0A6F4D' // green-700
    case 'requestChange':
      return '#962F22' // red-700
    default:
      return '#333333' // neutral-800
  }
}

const MediaIcon = ({ children }: { children: React.ReactNode }) => (
  <div className="absolute right-1 top-1 z-[1] grid size-6 place-items-center rounded-full bg-black/40 [&_svg]:w-[14px] [&_svg]:fill-white">
    {children}
  </div>
)

/**
 * SKE-3804: post card aligned to the dashboard approvals-board card
 * (board-post-card.tsx) — a clean two-column composition: the asset spans
 * the FULL card height flush on the left, all other info stacked in a
 * padded column on the right. Status is the primary element via a bold,
 * filled status pill at the top of that column (external review is a single
 * list, so status must stand out — the board can demote it to a faint pill
 * only because its columns are status-grouped).
 */
const UpcomingPostCard: React.FC<UpcomingPostCardProps> = ({
  status,
  statusLabel,
  time,
  accounts,
  caption,
  platform,
  isSelected,
  media,
}) => {
  // Inner content of the full-height media column.
  const renderMedia = (media: { src?: string; type: PostType }) => {
    const mediaIcon = {
      carousel: <CarouselIcon />,
      video: <VideoIcon />,
      reel: <ReelIcon />,
      story: <StoryIcon />,
      singleImage: null,
      text: null,
    }[media.type]

    if (media.type === 'text') {
      return (
        <div className="absolute inset-0 grid place-items-center text-neutral-300 [&_svg]:w-7">
          <TextIcon data-testid="text_icon" />
        </div>
      )
    }

    return (
      <>
        <img
          src={media.src}
          alt={media.type}
          className="absolute inset-0 size-full object-cover"
        />
        {mediaIcon && <MediaIcon>{mediaIcon}</MediaIcon>}
      </>
    )
  }

  const accountLengthLimit = media.type === 'text' ? 2 : 1
  const isDisplayTime =
    status !== 'requestChange' && time !== datePlaceholderForDraftPost
  const isResolved = status !== 'pending'

  const renderAccount = (
    account: UpcomingPostCardProps['accounts'][number],
    index: number
  ) => (
    <div
      key={account.name}
      data-testid="account_avatar"
      className="flex min-w-0 items-center gap-2 text-xs"
    >
      <div className="relative shrink-0">
        <AvatarComponent
          src={account.avatar}
          alt={account.name}
          className="size-8 min-w-8 overflow-hidden rounded-full border-2 border-solid [&_img]:size-full [&_img]:rounded-full [&_img]:object-cover"
          style={{ borderColor: account.avatarBorderColor || 'black' }}
        />
        {/* Platform badge on the primary avatar only (mirrors the board card).
            PlatformIcon renders the unknown-platform logo when platform is
            undefined, so it's always present for one avatar. */}
        {index === 0 && (
          <span className="absolute -bottom-0.5 -right-0.5 grid size-[18px] place-items-center rounded-full bg-white shadow-sm [&_svg]:w-3">
            <PlatformIcon platform={platform} />
          </span>
        )}
      </div>
      <span className="truncate font-medium text-neutral-800">
        {account.name}
      </span>
    </div>
  )

  return (
    <div
      className={cn(
        'relative flex min-h-[116px] overflow-hidden rounded-lg bg-white shadow-card transition-shadow duration-150 hover:shadow-[0_4px_12px_0_rgba(16,24,40,0.12)]',
        isSelected && 'ring-2 ring-blue-500'
      )}
    >
      {isSelected && (
        <span data-testid="selected_indicator" className="sr-only" />
      )}

      {/* Media — full card height, flush to the left edge (board layout) */}
      <div
        data-testid="media"
        className="relative w-[84px] shrink-0 self-stretch overflow-hidden bg-neutral-50"
      >
        {renderMedia(media)}
      </div>

      {/* Content — everything else, stacked to the right */}
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-2 p-3',
          isResolved && 'opacity-70'
        )}
      >
        {/* Status (primary) + scheduled time */}
        <div className="flex items-center gap-2">
          <span
            className="inline-flex min-w-0 max-w-full items-center truncate rounded-full px-2.5 py-0.5 text-xs font-medium"
            style={{
              background: handleStatusStateBg(status, false),
              color: handleStatusStateColor(status),
            }}
          >
            {/* SKE-3798: pending shows the simple "Pending", not the verbose
                "Ready to review (Client)" status label. */}
            {status === 'pending'
              ? 'Pending'
              : statusLabel || startCase(status)}
          </span>
          {isDisplayTime && (
            <span className="ml-auto flex shrink-0 items-center gap-1 text-xs font-medium text-neutral-600 [&_svg]:size-[13px] [&_svg]:stroke-current">
              <ClockIcon />
              {time}
            </span>
          )}
        </div>

        {/* Account(s) */}
        {accounts.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex min-w-0 flex-col gap-1">
              {accounts
                .filter((_, index) => index < accountLengthLimit)
                .map(renderAccount)}
            </div>
            {accounts.length > accountLengthLimit && (
              <span
                data-testid="excessive_account_count"
                className="shrink-0 text-xs font-medium text-neutral-600"
              >
                +{accounts.length - accountLengthLimit}
              </span>
            )}
          </div>
        )}

        {/* Caption */}
        <div className="line-clamp-2 overflow-hidden text-xs leading-4 text-neutral-600">
          <RenderSkedCaption
            caption={caption}
            targetPlatform={deMapPlatform(platform ?? '')}
          />
        </div>
      </div>
    </div>
  )
}

export default UpcomingPostCard
